<template>
  <b-row class="OBView">
    <b-col cols="12" lg="10" offset-lg="1">
      <Notification v-bind.sync="notification" />
      <Notification v-bind.sync="approvalNotification" />

      <OBCard
        :no-submit="true"
        :readonly="true"
        :employee="userEmployeeData"
        :others="otherData"
      />
    </b-col>
  </b-row>
</template>
<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import OBCard from '@/components/OBCard';
import Notification from '@/components/Notification';
import FormService from '@/services/FormService';
import obStatuses from '@/mixins/obStatuses';

const form = new FormService();

export default {
  name: 'OBView',
  mixins: [obStatuses],
  components: {
    Notification,
    OBCard,
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      const { key } = vm.$route.params;
      const { id } = vm.userData;

      vm.loadingOBData = true;
      form.checkIfUser('ob', id, key)
        .then(({ success, message, isUser }) => {
          if (!success || !isUser) {
            vm.setAlert({
              show: 5,
              variant: 'danger',
              fade: true,
              dismissible: true,
              message: typeof message === 'string' ? message : 'An error occurred',
              icon: {
                name: 'exclamation',
              },
            });
          }
          return success;
        })
        .catch(() => {
          vm.setAlert({
            show: 5,
            variant: 'danger',
            fade: true,
            dismissible: true,
            message: 'An error occurred',
            icon: {
              name: 'exclamation',
            },
          });

          return false;
        })
        .then((success) => {
          if (!success) {
            return vm.$router.push({ name: 'home' });
          }

          // User is the verified approver, get timesheet data
          vm.startLoadingPage();
          return form.getFormDataByKey('ob', key);
        })
        .then(vm.displayData)
        .then(() => {
          vm.stopLoadingPage();
          vm.loadingOBData = false;
        });
    });
  },
  data() {
    return {
      loadingApproveReject: false,
      loadingOBData: false,
      userEmployeeData: {},
      obData: {},
      confirmModalOptions: {},
      comments: '',
    };
  },
  computed: {
    approvalNotification() {
      const options = { show: !this.loadingOBData };

      const otherOptions = {};
      switch (this.$get(this.obData, 'statusId', -1)) {
        case this.pendingApproval:
          otherOptions.variant = 'light';
          otherOptions.message = 'OB application has been submitted for approval.';
          otherOptions.icon = { name: 'regular/hourglass' };
          break;
        case this.approved:
          otherOptions.variant = 'success';
          otherOptions.message = 'OB application has been approved.';
          otherOptions.icon = { name: 'check' };
          break;
        case this.rejected:
          otherOptions.variant = 'danger';
          otherOptions.message = 'OB application has been rejected.';
          otherOptions.icon = { name: 'times-circle', scale: 1.5 };
          break;
        case this.cancelled:
          otherOptions.variant = 'light ';
          otherOptions.message = 'OB application has been cancelled.';
          otherOptions.icon = { name: 'ban' };
          break;
        default:
          otherOptions.variant = 'danger';
          otherOptions.message = 'An error occurred.';
          otherOptions.refresh = true;
          break;
      }

      return Object.assign({}, options, otherOptions);
    },
    otherData() {
      return {
        forApproval: true,
        loadingEmployee: this.loadingUserEmployeeData,
        obData: this.obData,
      };
    },
    approvalAction() {
      return this.$get(this.confirmModalOptions, 'action', '');
    },
    approvalNotifText() {
      const { full_name: name } = this.userEmployeeData;
      return `OB application for <strong class="text-uppercase">${name}</strong> is pending your approval.`;
    },
    currentStatus() {
      const status = this.$get(this.obData, 'status', null);
      const statusId = this.$get(this.obData, 'statusId', null);

      return !statusId ? null : { status, id: statusId };
    },
    ...mapGetters(['userData', 'isLoadingRequest']),
    ...mapState(['notification']),
  },
  methods: {
    displayData(result) {
      if (typeof result === 'object' && result) {
        const {
          success,
          user,
          ob,
          message,
        } = result;

        if (!success) {
          return this.setNotification({
            show: true,
            refresh: true,
            variant: 'danger',
            message: message || 'An error occurred.',
            icon: { name: 'exclamation-triangle' },
          });
        }

        const obData = {};
        const obKeys = Object.keys(ob || {});
        obKeys.forEach((k) => {
          if (k === 'totalHours' || k === 'nightDiff') {
            obData[k] = Number(ob[k] || 0).toFixed(2);
          } else {
            obData[k] = ob[k];
          }
        });

        this.obData = obData;
        this.userEmployeeData = user;
      }
      return false;
    },
    approveOrReject() {
      const action = this.approvalAction;
      if (!action) {
        // TODO: approval action not set
        return this.setAlert({
          show: 5,
          dismissible: true,
          variant: 'danger',
          message: 'Client error',
        });
      }
      // TODO: actual approve/reject
      const { key } = this.$route.params;
      const { id: approverId } = this.userData;

      this.loadingApproveReject = true;
      // Execute approval (or rejection) of ob
      return form.updateApprovalStatus('ob', action, key, approverId, this.comments)
        .then(({ success, message }) => {
          const defaultMessage = success ? `ob request ${action}ed` : 'An error occurred';

          this.setNotification({
            message: message || defaultMessage,
            show: true,
            dismissible: true,
            refresh: !success,
            variant: success ? 'success' : 'danger',
            icon: {
              name: success ? 'check' : 'exclamation-triangle',
            },
          });

          if (success) {
            // TODO: update current ob status
            switch (action) {
              case 'approve':
                this.obData = {
                  ...this.obData,
                  statusId: this.approved,
                  status: 'approved',
                };
                break;
              case 'reject':
                this.obData = {
                  ...this.obData,
                  statusId: this.rejected,
                  status: 'rejected',
                };
                break;
              default: break;
            }
          }
        })
        .catch(() => {
          this.setNotification({
            show: true,
            dismissible: true,
            refresh: true,
            variant: 'danger',
            message: 'An error occurred',
            icon: {
              name: 'exclamation-triangle',
            },
          });
        })
        .then(() => {
          this.loadingApproveReject = false;
        });
    },
    resetAction() {
      this.confirmModalOptions.action = '';
    },
    ...mapActions([
      'setAlert',
      'setNotification',
      'startLoadingRequest',
      'stopLoadingRequest',
      'startLoadingPage',
      'stopLoadingPage',
    ]),
  },
};
</script>
